import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Upload, Download, FileText, CheckCircle, AlertCircle, X, Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import * as XLSX from 'xlsx';
import { companyService } from '@/services/companyService';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { parseExpiryDate } from '@/lib/skuUtils';
import { decodeHtmlEntities } from '@/lib/utils';

const EXPIRY_CATEGORY: CategoryKey = 'expiry';

interface ImportRecord {
  barcode: string;
  expiryDate: string;
  parsedDate?: Date;
}

interface ColumnMapping {
  skuColumn: string | null;
  dateColumn: string | null;
  isValid: boolean;
}

interface ComparisonResult {
  status: 'MATCHED' | 'MISMATCHED' | 'NOT_FOUND';
  record: ImportRecord;
  systemProduct?: {
    sku_name: string;
    sku_name_chi: string;
    sku_name_sc: string;
    brand: string;
  };
  excelDate: Date;
  systemDate: Date | null;
}

interface ProcessingStats {
  totalRecords: number;
  validRecords: number;
  skippedRecords: number;
  matchedRecords: number;
  mismatchedRecords: number;
}

const BestExpiryDateImport = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({ skuColumn: null, dateColumn: null, isValid: false });
  const [validRecords, setValidRecords] = useState<ImportRecord[]>([]);
  const [comparisonResults, setComparisonResults] = useState<ComparisonResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState<'idle' | 'parsing' | 'filtering' | 'comparing' | 'complete'>('idle');

  // Progress tracking
  const [progress, setProgress] = useState({
    totalProducts: 0,
    processedProducts: 0,
    currentProduct: '',
  });
  const [stats, setStats] = useState<ProcessingStats>({
    totalRecords: 0,
    validRecords: 0,
    skippedRecords: 0,
    matchedRecords: 0,
    mismatchedRecords: 0
  });
  const [currentCompany, setCurrentCompany] = useState<{ id: string; code?: string; name?: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  useEffect(() => {
    (async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (company) setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
      } catch (e) {
        // non-blocking
      }
    })();
  }, []);

  // 解析 Excel 欄位
  const parseExcelColumns = (headers: string[]): ColumnMapping => {
    const SKU_NAMES = ['条码', 'Barcode'];
    const DATE_NAMES = ['批次号', '有效日期', 'Expiry Date'];

    let skuColumn = null;
    let dateColumn = null;

    // 按優先順序尋找欄位
    for (const name of SKU_NAMES) {
      if (headers.includes(name)) {
        skuColumn = name;
        break;
      }
    }

    for (const name of DATE_NAMES) {
      if (headers.includes(name)) {
        dateColumn = name;
        break;
      }
    }

    return {
      skuColumn,
      dateColumn,
      isValid: skuColumn !== null && dateColumn !== null
    };
  };

  // 解析 Excel 日期格式
  const parseImportExpiryDate = (dateStr: string): Date | null => {
    if (!dateStr || dateStr.toString().trim() === '') return null;

    const str = dateStr.toString().trim();

    // 優先處理完整日期格式 (YYYY/MM/DD 或 YYYY-MM-DD)
    const fullDateRegex = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/;
    const fullDateMatch = str.match(fullDateRegex);
    if (fullDateMatch) {
      const [, year, month, day] = fullDateMatch;
      const y = parseInt(year);
      const m = parseInt(month) - 1; // JavaScript months are 0-based
      const d = parseInt(day);

      // 驗證日期範圍（不限制年份）
      if (m >= 0 && m <= 11 && d >= 1 && d <= 31) {
        return new Date(y, m, d);
      }
    }

    // 處理只有年月的格式 (YYYY/MM) - 設為月初
    const monthOnlyRegex = /^(\d{4})\/(\d{1,2})$/;
    const monthOnlyMatch = str.match(monthOnlyRegex);
    if (monthOnlyMatch) {
      const [, year, month] = monthOnlyMatch;
      const y = parseInt(year);
      const m = parseInt(month) - 1; // JavaScript months are 0-based
      if (m >= 0 && m <= 11) {
        return new Date(y, m, 1);
      }
    }

    // 處理月份在前年份在後的格式 (MM/YYYY) - 設為月初
    const reverseMonthYearRegex = /^(\d{1,2})\/(\d{4})$/;
    const reverseMonthYearMatch = str.match(reverseMonthYearRegex);
    if (reverseMonthYearMatch) {
      const [, month, year] = reverseMonthYearMatch;
      const y = parseInt(year);
      const m = parseInt(month) - 1; // JavaScript months are 0-based
      if (m >= 0 && m <= 11) {
        return new Date(y, m, 1);
      }
    }

    // 最後嘗試標準 Date 建構函數 (處理其他可能的格式)
    const date = new Date(str);
    if (!isNaN(date.getTime())) {
      return date;
    }

    return null;
  };

  // 驗證和過濾記錄
  const validateAndFilterRecords = (records: any[], columnMapping: ColumnMapping): ImportRecord[] => {
    const validRecords: ImportRecord[] = [];
    let skippedCount = 0;

    for (const record of records) {
      // 檢查 SKU 欄位是否有資料
      const skuValue = record[columnMapping.skuColumn!];
      if (!skuValue || skuValue.toString().trim() === '') {
        skippedCount++;
        continue; // 跳過這行，繼續處理下一行
      }

      // 檢查日期欄位是否有資料
      const dateValue = record[columnMapping.dateColumn!];
      if (!dateValue || dateValue.toString().trim() === '') {
        skippedCount++;
        continue; // 跳過這行，繼續處理下一行
      }

      // 嘗試解析日期，如果解析失敗也跳過
      const parsedDate = parseImportExpiryDate(dateValue.toString());
      if (!parsedDate) {
        skippedCount++;
        continue; // 跳過這行，繼續處理下一行
      }

      // 資料有效，加入有效記錄列表
      validRecords.push({
        barcode: skuValue.toString().trim(),
        expiryDate: dateValue.toString().trim(),
        parsedDate: parsedDate
      });
    }

    console.log(`處理完成: ${validRecords.length} 筆有效記錄，跳過 ${skippedCount} 筆無效記錄`);

    return validRecords;
  };

  // 找出 SKU 重複的最佳記錄
  const findBestExpiryRecord = (skuRecords: ImportRecord[]): ImportRecord => {
    if (skuRecords.length === 1) return skuRecords[0];

    // 找出最接近今天的日期記錄
    const today = new Date();
    let bestRecord = skuRecords[0];
    let smallestDiff = Math.abs((skuRecords[0].parsedDate!.getTime() - today.getTime()));

    for (let i = 1; i < skuRecords.length; i++) {
      const record = skuRecords[i];
      const diff = Math.abs((record.parsedDate!.getTime() - today.getTime()));
      if (diff < smallestDiff) {
        smallestDiff = diff;
        bestRecord = record;
      }
    }

    return bestRecord;
  };

  // 比較系統資料
  const compareWithSystemData = async (importRecord: ImportRecord, companyId: string): Promise<ComparisonResult> => {
    // 建立唯一標識
    const companySkuKey = `${companyId}-${importRecord.barcode}`;

    // 查詢系統產品資料
    const { data: product } = await supabase
      .from('products')
      .select('sku_name, sku_name_chi, sku_name_sc, brand')
      .eq('company_sku_key', companySkuKey)
      .single();

    if (!product) {
      return {
        status: 'NOT_FOUND',
        record: importRecord,
        excelDate: importRecord.parsedDate!,
        systemDate: null
      };
    }

    // 使用 Task 022 的 parseExpiryDate 函數解析產品名稱中的日期
    const systemDate = parseExpiryDate(product.sku_name_chi || product.sku_name);

    // 比對日期（精確到日）
    const isSame = systemDate &&
                   importRecord.parsedDate!.getFullYear() === systemDate.getFullYear() &&
                   importRecord.parsedDate!.getMonth() === systemDate.getMonth() &&
                   importRecord.parsedDate!.getDate() === systemDate.getDate();

    return {
      status: isSame ? 'MATCHED' : 'MISMATCHED',
      record: importRecord,
      systemProduct: product,
      excelDate: importRecord.parsedDate!,
      systemDate: systemDate
    };
  };

  // 處理檔案
  const handleFileSelect = async (file: File) => {
    setIsProcessing(true);
    setProcessingStep('parsing');

    try {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            toast.error('檔案格式錯誤：至少需要標題行和一筆資料');
            setIsProcessing(false);
            setProcessingStep('idle');
            return;
          }

          // 取得標題行
          const headers = jsonData[0] as string[];
          const columnMapping = parseExcelColumns(headers);

          if (!columnMapping.isValid) {
            toast.error('找不到必要的欄位：条码/Barcode 和 批次号/有效日期/Expiry Date');
            setIsProcessing(false);
            setProcessingStep('idle');
            return;
          }

          setColumnMapping(columnMapping);
          toast.success('欄位識別成功');

          // 取得資料行（跳過標題行）
          const dataRows = jsonData.slice(1) as any[][];

          // 轉換為物件格式
          const records = dataRows.map(row => {
            const obj: any = {};
            headers.forEach((header, index) => {
              obj[header] = row[index] || '';
            });
            return obj;
          });

          setProcessingStep('filtering');

          // 驗證和過濾記錄
          const validRecords = validateAndFilterRecords(records, columnMapping);
          setValidRecords(validRecords);

          setStats(prev => ({
            ...prev,
            totalRecords: records.length,
            validRecords: validRecords.length,
            skippedRecords: records.length - validRecords.length
          }));

          toast.success(`資料解析完成：${validRecords.length} 筆有效記錄`);

          setProcessingStep('comparing');

          // 按 SKU 分組處理
          const skuGroups: { [key: string]: ImportRecord[] } = {};
          validRecords.forEach(record => {
            if (!skuGroups[record.barcode]) {
              skuGroups[record.barcode] = [];
            }
            skuGroups[record.barcode].push(record);
          });

          // 處理每個 SKU 組，找出最佳記錄
          const bestRecords: ImportRecord[] = [];
          Object.values(skuGroups).forEach(group => {
            const bestRecord = findBestExpiryRecord(group);
            bestRecords.push(bestRecord);
          });

          // 比較系統資料
          const results: ComparisonResult[] = [];
          const companyId = currentCompany?.id || '';

          // Initialize progress for comparison
          setProgress({
            totalProducts: bestRecords.length,
            processedProducts: 0,
            currentProduct: language === 'zh'
              ? `開始比對 ${bestRecords.length} 筆記錄...`
              : `Starting comparison of ${bestRecords.length} records...`,
          });

          for (let i = 0; i < bestRecords.length; i++) {
            const record = bestRecords[i];

            // Update progress
            setProgress(prev => ({
              ...prev,
              processedProducts: i + 1,
              currentProduct: language === 'zh'
                ? `比對中: ${record.barcode}`
                : `Comparing: ${record.barcode}`,
            }));

            const result = await compareWithSystemData(record, companyId);
            results.push(result);
          }

          setComparisonResults(results);

          // 統計結果
          const matched = results.filter(r => r.status === 'MATCHED').length;
          const mismatched = results.filter(r => r.status === 'MISMATCHED').length;

          setStats(prev => ({
            ...prev,
            matchedRecords: matched,
            mismatchedRecords: mismatched
          }));

          setProcessingStep('complete');
          toast.success('處理完成！請查看結果摘要。');

        } catch (error: any) {
          toast.error('檔案解析失敗: ' + error.message);
          setProcessingStep('idle');
        } finally {
          setIsProcessing(false);
        }
      };

      reader.readAsArrayBuffer(file);

    } catch (error: any) {
      toast.error('檔案處理失敗: ' + error.message);
      setIsProcessing(false);
      setProcessingStep('idle');
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setColumnMapping({ skuColumn: null, dateColumn: null, isValid: false });
    setValidRecords([]);
    setComparisonResults([]);
    setProcessingStep('idle');
    setStats({
      totalRecords: 0,
      validRecords: 0,
      skippedRecords: 0,
      matchedRecords: 0,
      mismatchedRecords: 0
    });
    setProgress({
      totalProducts: 0,
      processedProducts: 0,
      currentProduct: '',
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    // Create Excel template with proper text formatting for first column
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([
      ['条码', '批次号'],
      ['ABC123', '2026/03'],
      ['DEF456', '05/2026'], // MM/YYYY format now supported
      ['GHI789', '2026-03-01'],
      ['001234', '2027/01'], // Example with leading zeros
      ['056789', '12/2027'] // Another MM/YYYY example
    ]);

    // Set column A (条码) as text to preserve leading zeros
    if (!worksheet['!cols']) worksheet['!cols'] = [];
    worksheet['!cols'][0] = { wch: 15, t: 'str' }; // Column A width and text type
    worksheet['!cols'][1] = { wch: 12 }; // Column B width

    // Ensure first column values are treated as text
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:B5');
    for (let row = 1; row <= range.e.r; row++) { // Start from row 1 (skip header)
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].t = 'str'; // Force as string
        worksheet[cellAddress].w = worksheet[cellAddress].v?.toString(); // Set formatted text
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    XLSX.writeFile(workbook, 'best_expiry_import_template.xlsx');

    toast.success(language === 'zh' ? '模板檔案已下載' : 'Template file downloaded');
  };

  const exportMismatchedRecords = () => {
    const mismatchedRecords = comparisonResults.filter(r => r.status === 'MISMATCHED');

    if (mismatchedRecords.length === 0) {
      toast.error(language === 'zh' ? '沒有不一致的記錄可匯出' : 'No mismatched records to export');
      return;
    }

    // Create Excel data
    const excelData = [
      ['SKU ID', '品牌', '產品名稱', 'Excel 到期日', '系統到期日']
    ];

    mismatchedRecords.forEach(result => {
      const productName = language === 'zh'
        ? (result.systemProduct?.sku_name_chi || result.systemProduct?.sku_name || '')
        : (result.systemProduct?.sku_name || result.systemProduct?.sku_name_chi || '');
      excelData.push([
        result.record.barcode,
        decodeHtmlEntities(result.systemProduct?.brand || '-'),
        decodeHtmlEntities(productName),
        result.record.expiryDate,
        result.systemDate
          ? `${result.systemDate.getFullYear()}-${(result.systemDate.getMonth() + 1).toString().padStart(2, '0')}-${result.systemDate.getDate().toString().padStart(2, '0')}`
          : (language === 'zh' ? '無' : 'None')
      ]);
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths and types
    worksheet['!cols'] = [
      { wch: 15, t: 'str' }, // SKU ID - set as string to preserve leading zeros
      { wch: 12 }, // 品牌
      { wch: 40 }, // 產品名稱
      { wch: 15 }, // Excel 到期日
      { wch: 15 }  // 系統到期日
    ];

    // Ensure SKU ID column (column 0) values are treated as text to preserve leading zeros
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:E1');
    for (let row = 1; row <= range.e.r; row++) { // Start from row 1 (skip header)
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].t = 'str'; // Force as string
        worksheet[cellAddress].w = worksheet[cellAddress].v?.toString(); // Set formatted text
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Mismatched Records');
    XLSX.writeFile(workbook, `best_expiry_mismatched_records_${new Date().toISOString().split('T')[0]}.xlsx`);

    toast.success(language === 'zh' ? '不一致記錄已匯出' : 'Mismatched records exported');
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            onClick={() => {
              localStorage.setItem('selectedCategory', EXPIRY_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            variant="outline"
            className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'zh' ? '返回到期日總表' : 'Back to Expiry Summary'}
          </Button>
          <div className="flex items-center gap-2">
            <Building2 className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              {language === 'zh' ? '匯入功能(最佳使用日期)' : 'Import (Best Expiry Date)'}
            </h1>
          </div>
        </div>

        {/* Upload Section */}
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">{language === 'zh' ? '上傳檔案' : 'Upload File'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Template Download */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-purple-400" />
                  <div>
                    <p className="text-foreground font-medium">{language === 'zh' ? '下載模板' : 'Download Template'}</p>
                    <p className="text-muted-foreground text-sm">{language === 'zh' ? '下載 Excel 模板檔案以進行匯入' : 'Download Excel template file for import'}</p>
                  </div>
                </div>
                <Button onClick={downloadTemplate} variant="outline" className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50">
                  <Download className="w-4 h-4 mr-2" />
                  {language === 'zh' ? '下載模板' : 'Download Template'}
                </Button>
              </div>

              {/* File Upload */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setSelectedFile(file);
                        handleFileSelect(file);
                      }
                    }}
                    className="hidden"
                    id="best-expiry-import-file"
                    aria-label={language === 'zh' ? '選擇匯入檔案（Excel）' : 'Select import file (Excel)'}
                    title={language === 'zh' ? '選擇匯入檔案（Excel）' : 'Select import file (Excel)'}
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    disabled={isProcessing}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {language === 'zh' ? '選擇檔案' : 'Choose File'}
                  </Button>

                  {selectedFile && (
                    <div className="flex items-center gap-2">
                      <span className="text-foreground">{selectedFile.name}</span>
                      <Badge variant="outline" className="text-muted-foreground border-border">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={resetImport}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {isProcessing && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
                    <span>
                      {language === 'zh' ? '處理中...' : 'Processing...'} {stats.totalRecords > 0 ? `${stats.validRecords}/${stats.totalRecords}` : ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>


        {/* Processing Status */}
        {processingStep !== 'idle' && (
          <Card className="bg-card/80 border-border mb-6">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-400" />
                {language === 'zh' ? '處理狀態' : 'Processing Status'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${processingStep === 'parsing' ? 'bg-blue-400 animate-pulse' : 'bg-green-400'}`}></div>
                  <span className="text-muted-foreground">{language === 'zh' ? '解析檔案' : 'Parsing File'}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${processingStep === 'filtering' ? 'bg-blue-400 animate-pulse' : processingStep === 'parsing' ? 'bg-muted' : 'bg-green-400'}`}></div>
                  <span className="text-muted-foreground">{language === 'zh' ? '驗證資料' : 'Validating Data'}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${processingStep === 'comparing' ? 'bg-blue-400 animate-pulse' : processingStep === 'idle' || processingStep === 'parsing' || processingStep === 'filtering' ? 'bg-muted' : 'bg-green-400'}`}></div>
                  <span className="text-muted-foreground">{language === 'zh' ? '比對系統資料' : 'Comparing with System'}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${processingStep === 'complete' ? 'bg-green-400' : 'bg-muted'}`}></div>
                  <span className="text-muted-foreground">{language === 'zh' ? '處理完成' : 'Processing Complete'}</span>
                </div>

                {processingStep === 'comparing' && progress.totalProducts > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-center mb-4">
                      <Loader2 className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mr-3" />
                      <div className="text-center">
                        <p className="text-muted-foreground text-lg">
                          {language === 'zh'
                            ? '數據即時運算中。請勿離開此畫面 ！ 如運算過程被中斷，請重新載入此頁，再選擇檔案。'
                            : 'Data is being processed in real-time. Please do not leave this page! If the process is interrupted, please reload this page and select the file again.'
                          }
                        </p>
                        <p className="text-muted-foreground text-sm mt-1">{progress.currentProduct}</p>
                      </div>
                    </div>

                    <div className="max-w-md mx-auto">
                      <div className="flex justify-between text-sm text-muted-foreground mb-2">
                        <span>
                          {language === 'zh' ? '進度' : 'Progress'}: {progress.processedProducts} / {progress.totalProducts}
                        </span>
                        <span>
                          {Math.round((progress.processedProducts / progress.totalProducts) * 100)}%
                        </span>
                      </div>
                      <Progress
                        value={(progress.processedProducts / progress.totalProducts) * 100}
                        className="h-3 bg-muted"
                      />
                      <p className="text-center text-muted-foreground text-sm mt-2">
                        {language === 'zh'
                          ? `預計還有 ${Math.max(0, progress.totalProducts - progress.processedProducts)} 筆記錄`
                          : `Estimated ${Math.max(0, progress.totalProducts - progress.processedProducts)} records remaining`
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results Summary */}
        {processingStep === 'complete' && (
          <Card className="bg-card/80 border-border mb-6">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <CardTitle className="text-foreground">
                  {language === 'zh' ? '結果摘要' : 'Results Summary'}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Statistics */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-muted-foreground text-sm">{language === 'zh' ? '總記錄' : 'Total Records'}</p>
                    <p className="text-foreground text-2xl font-bold">{stats.totalRecords}</p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-muted-foreground text-sm">{language === 'zh' ? '有效記錄' : 'Valid Records'}</p>
                    <p className="text-green-400 text-2xl font-bold">{stats.validRecords}</p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-muted-foreground text-sm">{language === 'zh' ? '跳過記錄' : 'Skipped Records'}</p>
                    <p className="text-yellow-400 text-2xl font-bold">{stats.skippedRecords}</p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-muted-foreground text-sm">{language === 'zh' ? '匹配記錄' : 'Matched Records'}</p>
                    <p className="text-blue-400 text-2xl font-bold">{stats.matchedRecords}</p>
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-muted-foreground text-sm">{language === 'zh' ? '需注意項目' : 'Items to Review'}</p>
                    <p className="text-red-400 text-2xl font-bold">{stats.mismatchedRecords}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mismatched Records */}
        {comparisonResults.filter(r => r.status === 'MISMATCHED').length > 0 && (
          <Card className="bg-card/80 border-border mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-red-400 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  {language === 'zh' ? '提醒列表' : 'Reminders List'} ({comparisonResults.filter(r => r.status === 'MISMATCHED').length})
                </CardTitle>
                <Button
                  onClick={exportMismatchedRecords}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {language === 'zh' ? '匯出 Excel' : 'Export Excel'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-muted/60">
                    <TableRow>
                      <TableHead className="text-muted-foreground w-24">{language === 'zh' ? 'SKU ID' : 'SKU ID'}</TableHead>
                      <TableHead className="text-muted-foreground w-24">{language === 'zh' ? '品牌' : 'Brand'}</TableHead>
                      <TableHead className="text-muted-foreground min-w-48">{language === 'zh' ? '產品名稱' : 'Product Name'}</TableHead>
                      <TableHead className="text-muted-foreground w-32">{language === 'zh' ? 'Excel 到期日' : 'Excel Expiry Date'}</TableHead>
                      <TableHead className="text-muted-foreground w-32">{language === 'zh' ? '系統到期日' : 'System Expiry Date'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comparisonResults
                      .filter(r => r.status === 'MISMATCHED')
                      .map((result, index) => (
                        <TableRow key={index} className="border-border hover:bg-accent/30">
                          <TableCell className="text-foreground font-mono text-sm">{result.record.barcode}</TableCell>
                          <TableCell className="text-muted-foreground">{decodeHtmlEntities(result.systemProduct?.brand || '-')}</TableCell>
                          <TableCell className="text-muted-foreground max-w-xs">
                            <div className="line-clamp-2">
                              {decodeHtmlEntities(
                                language === 'zh'
                                  ? (result.systemProduct?.sku_name_chi || result.systemProduct?.sku_name || '')
                                  : (result.systemProduct?.sku_name || result.systemProduct?.sku_name_chi || '')
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-yellow-400 font-mono text-sm">{result.record.expiryDate}</TableCell>
                          <TableCell className="text-blue-400 font-mono text-sm">
                            {result.systemDate ? `${result.systemDate.getFullYear()}-${(result.systemDate.getMonth() + 1).toString().padStart(2, '0')}-${result.systemDate.getDate().toString().padStart(2, '0')}` : (language === 'zh' ? '無' : 'None')}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
};

export default BestExpiryDateImport;
